import posthog, { type CaptureResult, type PostHog } from "posthog-js";

import type {
  AnalyticsProps,
  CaptureArgs,
  EventName,
  FlagKey,
  FlagPayload,
  FlagVariant,
} from "./analytics.types";

/**
 * The only file that imports `posthog-js` (ESLint enforces it). A module-level
 * singleton like `http.service.ts`: every method is a no-op until `init()` runs
 * with a key, so Storybook, tests and dashboards without `VITE_POSTHOG_KEY`
 * cost nothing. Identity (identify / reset) is owned by
 * `src/platform/analytics/AnalyticsSession.tsx`; feature code only captures
 * events from its typed catalog and reads flags through the platform hooks.
 */
export interface AnalyticsInitOptions {
  /** PostHog project token; empty disables everything. */
  key: string;
  /** Ingestion host; US Cloud when empty. */
  host?: string;
  /** `development` | `staging` | `qa` | `production` — the `env` super property. */
  env: string;
  /** `tenko` | `monokoro` — the `app` super property. */
  appId: string;
  appVersion?: string;
  /** Short git commit (`''` when unknown). */
  commit: string;
  /** Session replay is recorded only when `true`. */
  isProduction: boolean;
  /** `vite dev`: enables `window.__analytics` (flag overrides, debug). */
  isLocal: boolean;
  /** Hostnames that receive `x-posthog-session-id` / `x-posthog-distinct-id` (our API). */
  tracingHosts?: string[];
}

const DEFAULT_HOST = "https://us.i.posthog.com";
const UI_HOST = "https://us.posthog.com";
/** One-time login tokens and OAuth payloads travel in the URL; they never reach PostHog. */
const SENSITIVE_QUERY_KEYS = ["token", "state", "code", "access_token", "id_token", "challenge_token"];
const URL_PROPERTIES = ["$current_url", "$referrer", "$initial_current_url", "$initial_referrer"];

let client: PostHog | null = null;
let baseProperties: AnalyticsProps = {};
let isLocalBuild = false;
let flagsVersion = 0;
let flagsLoaded = false;
const flagListeners = new Set<() => void>();
const timers = new Map<string, number>();

const redactUrl = (value: unknown): unknown => {
  if (typeof value !== "string" || !(value.includes("?") || value.includes("#"))) return value;
  try {
    const url = new URL(value, window.location.origin);
    let touched = false;
    for (const key of SENSITIVE_QUERY_KEYS) {
      if (url.searchParams.has(key)) {
        url.searchParams.set(key, "[redacted]");
        touched = true;
      }
    }
    if (url.hash) {
      url.hash = "";
      touched = true;
    }
    return touched ? url.toString() : value;
  } catch {
    return value;
  }
};

const scrub = (result: CaptureResult | null): CaptureResult | null => {
  if (!result) return result;
  for (const property of URL_PROPERTIES) {
    if (property in result.properties) {
      result.properties[property] = redactUrl(result.properties[property]);
    }
    if (result.$set_once && property in result.$set_once) {
      result.$set_once[property] = redactUrl(result.$set_once[property]);
    }
  }
  return result;
};

const notifyFlagListeners = () => {
  flagsVersion += 1;
  flagListeners.forEach((listener) => listener());
};

type FlagOverrides = Partial<{ [K in FlagKey]: FlagVariant<K> }>;

const exposeDevTools = (instance: PostHog) => {
  (window as unknown as { __analytics?: unknown }).__analytics = {
    overrideFlags: (overrides: FlagOverrides | false) => analytics.overrideFlags(overrides),
    debug: (enabled = true) => instance.debug(enabled),
    client: instance,
  };
};

export const analytics = {
  init(options: AnalyticsInitOptions): void {
    // HMR re-runs bootstrap; posthog must be initialised exactly once.
    if (client || !options.key) return;
    isLocalBuild = options.isLocal;
    baseProperties = {
      app: options.appId,
      env: options.env,
      commit: options.commit,
      app_version: options.appVersion,
      is_local: options.isLocal,
    };
    // Assigned before init: with the npm bundle `loaded` fires synchronously.
    client = posthog;
    posthog.init(options.key, {
      api_host: options.host || DEFAULT_HOST,
      ui_host: UI_HOST,
      defaults: "2026-08-30",
      capture_pageview: "history_change",
      capture_pageleave: "if_capture_pageview",
      person_profiles: "identified_only",
      // Clicks on buttons and links only: bounded event volume (free-plan quota),
      // heatmaps still work; every business signal is an explicit event.
      autocapture: { dom_event_allowlist: ["click"], element_allowlist: ["button", "a"] },
      capture_dead_clicks: true,
      // Sentry owns errors.
      capture_exceptions: false,
      disable_session_recording: !options.isProduction,
      session_recording: {
        maskAllInputs: true,
        maskTextSelector: "[data-ph-mask]",
        blockClass: "ph-no-capture",
      },
      feature_flag_request_timeout_ms: 3000,
      __add_tracing_headers: options.tracingHosts,
      before_send: scrub,
      // The callback receives the narrower PostHogInterface; the singleton is the same object.
      loaded: () => {
        posthog.register(baseProperties);
        if (isLocalBuild) exposeDevTools(posthog);
      },
    });
    posthog.onFeatureFlags(() => {
      flagsLoaded = true;
      notifyFlagListeners();
    });
  },

  isEnabled(): boolean {
    return client !== null;
  },

  capture<E extends EventName>(event: E, ...args: CaptureArgs<E>): void {
    if (!client) return;
    const [props, options] = args;
    client.capture(event, {
      ...props,
      ...(options?.set && { $set: options.set }),
      ...(options?.setOnce && { $set_once: options.setOnce }),
    });
  },

  identify(distinctId: string, set?: AnalyticsProps, setOnce?: AnalyticsProps): void {
    client?.identify(distinctId, set, setOnce);
  },

  /** Person properties available to flag targeting (org- or role-based rollouts). */
  setPersonPropertiesForFlags(props: AnalyticsProps): void {
    client?.setPersonPropertiesForFlags(props);
  },

  /** Super properties: attached to every event until `unregister` or `reset`. */
  register(props: AnalyticsProps): void {
    client?.register(props);
  },

  unregister(key: string): void {
    client?.unregister(key);
  },

  /** New anonymous visitor (logout). `posthog.reset()` also drops super properties. */
  reset(): void {
    if (!client) return;
    client.reset();
    client.register(baseProperties);
    timers.clear();
  },

  /** Reads a flag; emits `$feature_flag_called`, the exposure experiments are computed from. */
  getFlag<K extends FlagKey>(key: K): FlagVariant<K> | undefined {
    if (!client) return undefined;
    return client.getFeatureFlag(key) as FlagVariant<K> | undefined;
  },

  getFlagPayload<K extends FlagKey>(key: K): FlagPayload<K> | undefined {
    if (!client) return undefined;
    return (client.getFeatureFlagPayload(key) ?? undefined) as FlagPayload<K> | undefined;
  },

  subscribeFlags(listener: () => void): () => void {
    flagListeners.add(listener);
    return () => flagListeners.delete(listener);
  },

  getFlagsVersion(): number {
    return flagsVersion;
  },

  /** `true` once flags arrived — and always when analytics is disabled, so callers render fallbacks. */
  flagsReady(): boolean {
    return client === null || flagsLoaded;
  },

  reloadFlags(): void {
    client?.reloadFeatureFlags();
  },

  /** Dev only: force variants locally (`false` clears). Persisted by posthog in localStorage. */
  overrideFlags(overrides: FlagOverrides | false): void {
    if (!client || !isLocalBuild) return;
    if (overrides === false) {
      client.featureFlags.overrideFeatureFlags(false);
    } else {
      const flags: Record<string, string | boolean> = {};
      for (const [key, value] of Object.entries(overrides)) {
        if (value !== undefined) flags[key] = value;
      }
      client.featureFlags.overrideFeatureFlags({ flags });
    }
    notifyFlagListeners();
  },

  startTimer(name: string): void {
    timers.set(name, performance.now());
  },

  /** Elapsed ms since `startTimer(name)`, or `undefined` when it never started (e.g. after a reload). */
  endTimer(name: string): number | undefined {
    const startedAt = timers.get(name);
    timers.delete(name);
    return startedAt === undefined ? undefined : Math.round(performance.now() - startedAt);
  },

  getDistinctId(): string | undefined {
    return client?.get_distinct_id();
  },

  /** For support links next to a Sentry event; `undefined` when replay is off. */
  getSessionReplayUrl(): string | undefined {
    if (!client) return undefined;
    try {
      return client.get_session_replay_url();
    } catch {
      return undefined;
    }
  },
};
